import type { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';
import { useEffect, useRef, type MutableRefObject } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_AXIS_CONFIG_APPLIED,
    PPA_WHITESPACE_CELL_CARDS_VIEWED,
    PPA_WHITESPACE_DATA_LOAD_SUCCEEDED,
    PPA_WHITESPACE_OVERLAY_HIDDEN,
    PPA_WHITESPACE_OVERLAY_RENDERED,
    PPA_WHITESPACE_VIEWED,
} from 'utils/mixpanelEventNames';
import type { computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';

type GridMetrics = ReturnType<typeof computeWhitespaceGridMetrics>;

type UseWhitespaceMixpanelEffectsParams = {
    isMetaLoading: boolean;
    isGridFetched: boolean;
    isGridFetching: boolean;
    xAxis: string;
    yAxis: string;
    crossSection: string;
    incrementValue: number;
    showWhitespaces: boolean;
    gridRows: WhitespaceGridRow[];
    baseProps: Record<string, unknown>;
    gridMetrics: GridMetrics;
    pageViewProps: Record<string, unknown>;
    configRefreshStartedAtRef: MutableRefObject<number | null>;
    hasTrackedCellCardsViewedRef: MutableRefObject<boolean>;
};

const useWhitespaceMixpanelEffects = ({
    isMetaLoading,
    isGridFetched,
    isGridFetching,
    xAxis,
    yAxis,
    crossSection,
    incrementValue,
    showWhitespaces,
    gridRows,
    baseProps,
    gridMetrics,
    pageViewProps,
    configRefreshStartedAtRef,
    hasTrackedCellCardsViewedRef,
}: UseWhitespaceMixpanelEffectsParams) => {
    const hasTrackedPageViewRef = useRef(false);
    const hasTrackedDataLoadRef = useRef(false);
    const previousShowWhitespacesRef = useRef(showWhitespaces);
    const previousGridFetchingRef = useRef(true);
    const pageLoadStartedAtRef = useRef(performance.now());

    useEffect(() => {
        if (isMetaLoading || hasTrackedPageViewRef.current) return;
        hasTrackedPageViewRef.current = true;
        trackEventMixpanel(PPA_WHITESPACE_VIEWED, pageViewProps);
    }, [isMetaLoading, pageViewProps]);

    useEffect(() => {
        if (!isGridFetched || isGridFetching || hasTrackedDataLoadRef.current) return;
        hasTrackedDataLoadRef.current = true;
        trackEventMixpanel(PPA_WHITESPACE_DATA_LOAD_SUCCEEDED, {
            ...baseProps,
            ...gridMetrics,
            load_time_ms: Math.round(performance.now() - pageLoadStartedAtRef.current),
        });
    }, [baseProps, gridMetrics, isGridFetched, isGridFetching]);

    useEffect(() => {
        const wasFetching = previousGridFetchingRef.current;
        const gridJustFinishedLoading = wasFetching && !isGridFetching && isGridFetched && gridRows.length > 0;

        if (isGridFetching) {
            hasTrackedCellCardsViewedRef.current = false;
        }

        if (gridJustFinishedLoading && !hasTrackedCellCardsViewedRef.current) {
            hasTrackedCellCardsViewedRef.current = true;
            trackEventMixpanel(PPA_WHITESPACE_CELL_CARDS_VIEWED, {
                visible_cell_count: gridMetrics.visible_cell_count,
                visible_opportunity_cell_count: gridMetrics.opportunity_cell_count,
                x_axis: xAxis,
                y_axis: yAxis,
                cross_section: crossSection,
                show_whitespaces: showWhitespaces,
            });
        }

        previousGridFetchingRef.current = isGridFetching;
    }, [
        crossSection,
        gridMetrics,
        gridRows.length,
        hasTrackedCellCardsViewedRef,
        isGridFetched,
        isGridFetching,
        showWhitespaces,
        xAxis,
        yAxis,
    ]);

    useEffect(() => {
        if (previousShowWhitespacesRef.current === showWhitespaces) return;

        if (showWhitespaces) {
            trackEventMixpanel(PPA_WHITESPACE_OVERLAY_RENDERED, {
                opportunity_cell_count: gridMetrics.opportunity_cell_count,
                overlay_type: 'border_highlight',
                badge_type: 'opportunity_icon',
                show_whitespaces: showWhitespaces,
            });
        } else {
            trackEventMixpanel(PPA_WHITESPACE_OVERLAY_HIDDEN, {
                hidden_opportunity_cell_count: gridMetrics.opportunity_cell_count,
                show_whitespaces: showWhitespaces,
            });
        }

        previousShowWhitespacesRef.current = showWhitespaces;
    }, [gridMetrics.opportunity_cell_count, showWhitespaces]);

    useEffect(() => {
        if (!isGridFetched || isGridFetching || configRefreshStartedAtRef.current == null) return;

        trackEventMixpanel(PPA_WHITESPACE_AXIS_CONFIG_APPLIED, {
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            increment_value: incrementValue,
            generated_bucket_count: gridMetrics.generated_bucket_count,
            total_cell_count: gridMetrics.total_cell_count,
            refresh_time_ms: Math.round(performance.now() - configRefreshStartedAtRef.current),
        });
        configRefreshStartedAtRef.current = null;
    }, [
        configRefreshStartedAtRef,
        crossSection,
        gridMetrics.generated_bucket_count,
        gridMetrics.total_cell_count,
        incrementValue,
        isGridFetched,
        isGridFetching,
        xAxis,
        yAxis,
    ]);
};

export default useWhitespaceMixpanelEffects;
